import React, { useState, useMemo } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSupplierPaymentTerms } from '@/api/rebates/useReports';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import { SingleSelect } from '@/components/common/SingleSelect';
import type { SupplierPaymentTermsItem } from '@erp/shared-types';
import { ShareNetworkIcon, LinkIcon } from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const REPORT_ID = 'supplier-payment-terms';
const REPORT_TITLE = 'Supplier Payment Terms Report';

const SupplierPaymentTermsPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useSupplierPaymentTerms({
    year: selectedYear,
  });

  // Define table columns
  const columns: TableColumn<SupplierPaymentTermsItem>[] = [
    {
      id: 'supplier',
      label: 'Supplier',
      sortable: true,
    },
    {
      id: 'agreement',
      label: 'Agreement',
      sortable: true,
    },
    {
      id: 'rebatePaymentTerms',
      label: 'Rebate Payment Terms',
      sortable: true,
    },
    {
      id: 'purchasePaymentTerms',
      label: 'Purchase Payment Terms',
      sortable: true,
    },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              Supplier Payment Terms Report - {selectedYear}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              View payment terms for all supplier agreements overlapping the
              selected year
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Create share link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Manage shared links
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6">
              <SingleSelect
                label="Year"
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder="Select year..."
                searchPlaceholder="Search years..."
                shouldAutoSort={false}
              />
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Supplier Payment Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-24 flex items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading...</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive">
                Error loading report data. Please try again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Table */}
        {report && !isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Supplier Payment Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <GentlyTable
                data={report.data}
                columns={columns}
                searchPlaceholder="Search suppliers, agreements, or payment terms..."
                searchFields={[
                  'supplier',
                  'agreement',
                  'rebatePaymentTerms',
                  'purchasePaymentTerms',
                ]}
                emptyMessage="No payment terms found for the selected year"
                showDownload={true}
                downloadFilename={`supplier-payment-terms-${selectedYear}`}
                showCount={true}
                defaultSort={{ sortBy: 'supplier', sortDirection: 'asc' }}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
    </DashboardLayout>
  );
};

export default SupplierPaymentTermsPage;
